import { Controller } from "@hotwired/stimulus"

// Composer UX for conversations/show.
//
// Behaviors:
//   1. autogrow(): re-compute textarea height on every input so the box grows
//      with content instead of scrolling internally on mobile.
//   2. keydown(): submit on Cmd+Enter (mac) or Ctrl+Enter (others). Shift+Enter
//      and plain Enter still insert a newline.
//   3. onInput(): autogrow + toggle the Send button's disabled state based on
//      whether the input has non-whitespace content.
//   4. turbo:submit-end → clear(): wipe the textarea + reset Send to disabled
//      after a successful submit. On failure, surface an inline error and
//      leave the user's draft intact so they can retry.
export default class extends Controller {
  static targets = ["form", "input", "submit", "error"]

  connect() {
    this.boundClear = this.handleSubmitEnd.bind(this)
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("turbo:submit-end", this.boundClear)
    }
    this.autogrow()
    this.refreshSubmit()
  }

  disconnect() {
    if (this.hasFormTarget && this.boundClear) {
      this.formTarget.removeEventListener("turbo:submit-end", this.boundClear)
    }
    if (this.errorTimeout) clearTimeout(this.errorTimeout)
  }

  // Called on every keystroke. Drives both autogrow and the Send-disabled
  // state from the same source of truth (the input's current value).
  onInput() {
    this.autogrow()
    this.refreshSubmit()
    // First keystroke after a failed send dismisses the stale error so the
    // user isn't staring at "couldn't send" while typing a fresh attempt.
    this.hideError()
  }

  autogrow() {
    if (!this.hasInputTarget) return
    const el = this.inputTarget
    el.style.height = "auto"
    // 200px ceiling keeps the composer from eating the message list on long
    // drafts; users can still scroll inside the textarea past that height.
    el.style.height = Math.min(el.scrollHeight, 200) + "px"
  }

  refreshSubmit() {
    if (!this.hasSubmitTarget || !this.hasInputTarget) return
    const hasContent = this.inputTarget.value.trim().length > 0
    this.submitTarget.disabled = !hasContent
  }

  keydown(event) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      if (this.hasFormTarget) {
        this.formTarget.requestSubmit()
      }
    }
  }

  handleSubmitEnd(event) {
    // turbo:submit-end exposes detail.success.
    if (event && event.detail && event.detail.success === false) {
      this.showError("Couldn't send. Please try again.")
      this.refreshSubmit()
      return
    }
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.autogrow()
    }
    this.refreshSubmit()
    this.hideError()
  }

  showError(text) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = text
    this.errorTarget.hidden = false
    if (this.errorTimeout) clearTimeout(this.errorTimeout)
    this.errorTimeout = setTimeout(() => this.hideError(), 4000)
  }

  hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.hidden = true
    if (this.errorTimeout) clearTimeout(this.errorTimeout)
  }
}
